/**
 * Packaging Charts Component - Display charts for thành phẩm đóng gói
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Cell,
  ReferenceLine
} from 'recharts';
import { 
  TrendingUp, 
  Package,
  BarChart3,
  Activity
} from 'lucide-react';
import { usePackagingData } from '@/hooks/useProductionData';
import { ProductionFilters } from '@/types/production';

interface PackagingChartsProps {
  filters: ProductionFilters;
}

export const PackagingCharts: React.FC<PackagingChartsProps> = ({ filters }) => {
  const [activeChart, setActiveChart] = useState<'container' | 'productivity'>('container');

  // Fetch packaging data
  const {
    data: packagingData = [],
    isLoading,
    error
  } = usePackagingData(filters);

  // Transform data for chart display (chỉ m3 thùng như yêu cầu)
  const chartData = React.useMemo(() => {
    if (!packagingData.length) {
      console.log('📦 No packaging data available for charts');
      return [];
    }

    console.log('📦 Raw packaging data for charts:', packagingData.length, 'records');
    console.log('📦 Sample packaging data:', packagingData[0]);

    return packagingData.map(item => {
      const date = new Date(item.date).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit'
      });

      const safeNumber = (value: any): number => {
        const num = Number(value);
        return isNaN(num) || num < 0 ? 0 : num;
      };

      if (activeChart === 'container') {
        // Hiển thị số container đóng được/ngày
        return {
          date,
          fullDate: item.date,
          'Container': safeNumber(item.containerCount),
          volumeCarton: safeNumber(item.volumeCarton), // Dành cho tooltip
        };
      } else { // productivity  
        // Chỉ hiển thị năng suất theo M3 thùng
        return {
          date,
          fullDate: item.date,
          'NS Thùng': safeNumber(item.productivityCarton),
        };
      }
    }).sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());
  }, [packagingData, activeChart]);

  // Chart colors
  const chartColors = {
    'Container': '#f59e0b', // Amber/Yellow
    'NS Thùng': '#8b5cf6', // Purple
  };

  // Calculate average values for trendlines based on working days logic
  const averageValue = React.useMemo(() => {
    if (!chartData.length) return 0;
    
    const activeKey = activeChart === 'container' ? 'Container' : 'NS Thùng';
    
    // Find working days based on original logic (any packaging value > 0)
    const workingDaysData = packagingData.filter(item => 
      (item.labor > 0) || (item.volumeCarton > 0) || 
      (item.volumeWood > 0) || (item.productivityCarton > 0) || 
      (item.productivityWood > 0) || (item.containerCount > 0)
    );
    
    if (workingDaysData.length === 0) return 0;
    
    // Calculate trendline from working days data (including zeros)
    const workingDaysValues = workingDaysData.map(item => {
      if (activeKey === 'Container') {
        return item.containerCount || 0;
      } else { // 'NS Thùng'
        return item.productivityCarton || 0;
      }
    });
    
    const sum = workingDaysValues.reduce((acc, val) => acc + val, 0);
    const average = sum / workingDaysValues.length;
    
    console.log(`📊 Packaging chart average for ${activeKey}:`, {
      totalDays: chartData.length,
      workingDays: workingDaysData.length,
      workingDaysValues,
      average: average.toFixed(3)
    });
    
    return average;
  }, [chartData, activeChart, packagingData]);

  // Chart metadata
  const chartMeta = {
    container: {
      title: 'Biểu đồ Công suất Container',
      description: 'Theo dõi số lượng container đóng được/ngày',
      unit: 'container',
      icon: Package,
      color: 'text-amber-600',
    },
    productivity: {
      title: 'Biểu đồ Năng suất Thùng Carton',
      description: 'Theo dõi năng suất đóng gói theo M³ thùng',
      unit: 'm³/công',
      icon: BarChart3,
      color: 'text-purple-600',
    },
  };

  const currentMeta = chartMeta[activeChart];
  const Icon = currentMeta.icon;

  // Custom label component for showing M³ thùng on container chart
  const VolumeLabel = (props: any) => {
    const { x, y, payload } = props;
    if (activeChart === 'container' && payload && payload.volumeCarton > 0) {
      return (
        <text 
          x={x} 
          y={y - 10} 
          fill="#10b981" 
          textAnchor="middle" 
          dominantBaseline="middle"
          fontSize={10}
          fontWeight="500"
        >
          {payload.volumeCarton.toFixed(1)}
        </text>
      );
    }
    return null;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const fullDate = payload[0]?.payload?.fullDate;
      const displayDate = fullDate ? new Date(fullDate).toLocaleDateString('vi-VN') : label;
      const volumeCarton = payload[0]?.payload?.volumeCarton;
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{displayDate}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              <span className="font-medium">{entry.dataKey}:</span>{' '}
              <span>{Number(entry.value).toFixed(activeChart === 'productivity' ? 2 : 1)} {currentMeta.unit}</span>
            </p>
          ))}
          {activeChart === 'container' && volumeCarton > 0 && (
            <p className="text-sm text-green-600 font-medium mt-1">
              <span className="font-medium">M³ Thùng:</span>{' '}
              <span>{volumeCarton.toFixed(1)} m³</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600">Lỗi tải biểu đồ đóng gói</CardTitle>
          <CardDescription>{error.message}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${currentMeta.color}`} />
            <div>
              <CardTitle>{currentMeta.title}</CardTitle>
              <CardDescription>{currentMeta.description}</CardDescription>
            </div>
          </div>
          
          {/* Chart Type Selector */}
          <div className="flex gap-2">
            <Button
              variant={activeChart === 'container' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveChart('container')}
              className="flex items-center gap-1"
            >
              <Package className="h-3 w-3" />
              Công suất Container
            </Button>
            <Button
              variant={activeChart === 'productivity' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveChart('productivity')}
              className="flex items-center gap-1"
            >
              <BarChart3 className="h-3 w-3" />
              Năng suất
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Activity className="h-4 w-4 animate-spin" />
              Đang tải dữ liệu biểu đồ đóng gói...
            </div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  stroke="#666"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#666"
                  domain={['dataMin - 1', 'dataMax + 1']}
                  allowDataOverflow={false}
                  tickFormatter={(value) => {
                    if (activeChart === 'productivity') {
                      return value.toFixed(2);
                    }
                    return value.toFixed(1);
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                
                {/* Trendline - Average Reference Line */}
                {averageValue > 0 && (
                  <ReferenceLine
                    y={averageValue}
                    stroke={activeChart === 'container' ? chartColors['Container'] : chartColors['NS Thùng']}
                    strokeDasharray="8 4"
                    strokeWidth={2.5}
                    strokeOpacity={0.8}
                    label={{
                      value: `TB: ${averageValue.toFixed(activeChart === 'productivity' ? 2 : 1)}`,
                      position: 'insideTopRight',
                      fontSize: 11,
                      fill: activeChart === 'container' ? chartColors['Container'] : chartColors['NS Thùng'],
                      fontWeight: 'bold'
                    }}
                  />
                )}
                
                {Object.keys(chartColors).map((key) => {
                  if ((activeChart === 'container' && key === 'Container') ||
                      (activeChart === 'productivity' && key === 'NS Thùng')) {
                    return (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={chartColors[key as keyof typeof chartColors]}
                        strokeWidth={3}
                        dot={{ r: 5, strokeWidth: 2, fill: chartColors[key as keyof typeof chartColors] }}
                        activeDot={{ r: 7, strokeWidth: 2, fill: chartColors[key as keyof typeof chartColors] }}
                        connectNulls={false}
                        name={key}
                      >
                        {activeChart === 'container' && (
                          <LabelList content={<VolumeLabel />} />
                        )}
                      </Line>
                    );
                  }
                  return null;
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Không có dữ liệu đóng gói để hiển thị biểu đồ</p>
              <p className="text-sm">Hãy thử điều chỉnh bộ lọc hoặc kiểm tra dữ liệu thành phẩm</p>
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
};